import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Camera, Settings, Images, ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useEventStore } from '@/store'

export default function HubPage() {
  const [hovered, setHovered] = useState<number | null>(null)
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)

  useEffect(() => {
    loadEvents().catch(console.error)
  }, [loadEvents])

  const cards = [
    {
      icon: Camera,
      title: 'Events',
      subtitle: `${events.length} event${events.length !== 1 ? 's' : ''}`,
      description: 'Create, edit, and launch photo booth events.',
      href: '/admin',
      bg: 'bg-white/5',
    },
    {
      icon: Settings,
      title: 'Admin',
      subtitle: 'Operator Panel',
      description: 'Configure cameras, layouts, effects, and sharing.',
      href: '/admin',
      bg: 'bg-white/5',
    },
    {
      icon: Images,
      title: 'Gallery',
      subtitle: 'Recap',
      description: 'Browse, download, and manage all captured sessions.',
      href: '/admin/gallery',
      bg: 'bg-white/5',
    },
  ]

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0b0b0a] text-white">


      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-4 flex items-center gap-3"
        >
          <h1>
            <img src="/logo-dark.png" alt="PoseBox" className="h-14 w-auto" />
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-12 text-center text-lg text-white/40"
        >
          Photo booth software for events
        </motion.p>

        {/* Cards */}
        <div className="grid w-full max-w-4xl gap-5 md:grid-cols-3">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.15, duration: 0.6 }}
            >
              <Link
                to={card.href}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="group relative flex flex-col rounded-lg border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 hover:bg-white/[0.06]"
              >

                <div className="relative z-10">
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg ${card.bg}`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>

                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-xl font-semibold">{card.title}</h2>
                    {i === 0 && (
                      <span className="flex h-5 items-center rounded bg-primary/15 px-2 text-[10px] font-medium text-primary">
                        {card.subtitle}
                      </span>
                    )}
                    {i !== 0 && (
                      <span className="text-xs text-white/30">{card.subtitle}</span>
                    )}
                  </div>

                  <p className="mb-6 text-sm leading-relaxed text-white/40">{card.description}</p>

                  <div className="mt-auto flex items-center gap-1 text-sm font-medium text-white/60 transition-colors group-hover:text-white">
                    Enter
                    <ArrowRight className={`h-4 w-4 transition-transform duration-300 ${hovered === i ? 'translate-x-1' : ''}`} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 text-xs text-white/20"
        >
          PoseBox
        </motion.div>
      </div>
    </div>
  )
}
